import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Dialog, DialogTitle, TextField, Button, IconButton, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useForm } from 'react-hook-form';
import Swal from 'sweetalert2';
import { saveMasterListSafely } from '../../Services/app/masterservice';

export default function ProcessGroupAdd({ open, handleClose, dialogOpenCount, datasource, customerId }) {
  const dialogBackgroundColor = dialogOpenCount === 0 ? '#f7f7f7' : '#ededed';
  const { register, handleSubmit, formState: { errors }, trigger, setValue, reset } = useForm({ mode: 'onBlur' });
  const defaultForm = useMemo(() => ({ groupName: '' }), []);
  const [formData, setFormData] = useState(defaultForm);
  const groupNameRef = useRef();

  useEffect(() => {
    if (!open) { reset(defaultForm); setFormData(defaultForm); }
  }, [open, reset, defaultForm]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setValue(name, value);
    trigger(name);
  };

  const onSubmit = async () => {
    try {
      const id = Math.random().toString(36).substr(2, 9);

      // Merge onto the FRESH server list (not the stale `datasource` prop), guard
      // against a wipe, and snapshot the previous list before writing.
      const response = await saveMasterListSafely({
        customerId,
        key: 'processgroups',
        baselineCount: Array.isArray(datasource) ? datasource.length : 0,
        mutate: (existingGroups) => {
          if (existingGroups.some(g => g.groupName?.trim().toLowerCase() === formData.groupName.trim().toLowerCase())) {
            const e = new Error('duplicate');
            e.code = 'DUP_GROUP';
            throw e;
          }
          const lastCode = existingGroups.length ? Math.max(...existingGroups.map(g => parseInt(g.code, 10) || 0)) : 0;
          existingGroups.push({ id, groupName: formData.groupName.trim(), code: String(lastCode + 1) });
          return existingGroups;
        },
      });
      Swal.fire('Success', response?.msg || 'Process Group created successfully', 'success');
      handleClose();
      reset(defaultForm);
    } catch (err) {
      if (err.code === 'DUP_GROUP') {
        Swal.fire('Error', 'Duplicate Process Group name not allowed.', 'error');
        return;
      }
      if (err.code === 'MASTER_SHRINK_GUARD') {
        Swal.fire('Save blocked to prevent data loss', 'The existing group list couldn\'t be read correctly, so saving now would have erased it. Nothing was changed — please check your connection and try again.', 'warning');
        return;
      }
      console.error(err);
      Swal.fire('Error', err.message, 'error');
    }
  };

  useEffect(() => {
    if (open && groupNameRef.current) setTimeout(() => groupNameRef.current.focus(), 100);
  }, [open]);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth PaperProps={{ style: { backgroundColor: dialogBackgroundColor, maxWidth: '500px' } }}>
      <DialogTitle style={{ color: 'black' }}>Add Process Group</DialogTitle>
      <div className="close_modal">
        <Tooltip title="Close">
          <IconButton onClick={handleClose} style={{ backgroundColor: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </div>
      <div className="machinedialog">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div style={{ margin: '1rem' }}>
            <TextField
              inputRef={groupNameRef}
              {...register('groupName', { required: 'Group Name is required', maxLength: { value: 100, message: 'Maximum 100 characters' } })}
              label="Group Name"
              value={formData.groupName}
              onChange={handleFormChange}
              onBlur={() => trigger('groupName')}
              fullWidth
              error={!!errors.groupName}
              helperText={errors.groupName?.message}
              InputLabelProps={{ required: true, sx: { color: 'black', '&.Mui-focused': { color: 'orange' } } }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: 'black' },
                  '&:hover fieldset': { borderColor: 'black' },
                  '&.Mui-focused fieldset': { borderColor: 'orange' },
                  '& .MuiOutlinedInput-input': { color: 'black' },
                },
              }}
            />
          </div>
          <div align="end" style={{ margin: '0.8rem 1rem 1rem' }}>
            <Button type="submit" variant="contained" color="warning">Save</Button>
          </div>
        </form>
      </div>
    </Dialog>
  );
}
